import { LinkGuideLayout } from '../components/LinkGuideLayout';

const sections = [
  ['prepare', 'Start on Windows'],
  ['import', 'Import sessions into Paddiq'],
  ['mappings', 'Match drivers, vehicles and tracks'],
  ['rename', 'Rename a Race Studio driver or vehicle'],
  ['export', 'Export sessions to Race Studio'],
  ['automatic', 'Automatically copy MyChron downloads'],
  ['storage', 'Check folders and naming'],
  ['troubleshoot', 'Resolve a missing session or failed transfer'],
] as const;

export function PaddiqLinkRaceStudioPage() {
  return (
    <LinkGuideLayout
      path="/resources/paddiq-link-race-studio"
      title="Use Race Studio with Paddiq Link"
      lead="Import sessions into Paddiq, export them to Race Studio, and keep driver and vehicle names matched."
      sections={sections}
    >
      <section className="guide-step" id="prepare">
        <div>
          <h2>Start on Windows.</h2>
          <p>
            Open <strong>Integrations → Race Studio</strong> in Link. This integration is Windows
            only; Link’s other MyChron features and optional file copies also work on Mac. Use a
            build that includes these controls and confirm the Paddiq workspace you want to use.
          </p>
          <ul>
            <li>
              <strong>Import:</strong> session files on this PC → Paddiq.
            </li>
            <li>
              <strong>Export:</strong> retained Paddiq originals → Race Studio on this PC.
            </li>
            <li>
              <strong>Mappings:</strong> connect Race Studio names to Paddiq profiles.
            </li>
            <li>
              <strong>Storage &amp; copies:</strong> check locations, naming and optional file
              copies.
            </li>
          </ul>
          <p>
            Export needs an existing Race Studio installation and session database. If you have just
            installed Race Studio, open it once to create its library. Direct registration currently
            supports the verified MyChron6 recording format and database schema 2.1.5, tested with
            Race Studio 3.72.27. Unsupported formats or database versions are reported before
            registration.
          </p>
        </div>
      </section>
      <section className="guide-step" id="import">
        <div>
          <h2>Import sessions into Paddiq.</h2>
          <ol className="guide-instructions">
            <li>
              Open <strong>Import</strong>. The table loads from Race Studio’s session folder. Use
              the folder button to choose another folder of XRK/XRZ files, or refresh to rescan.
            </li>
            <li>
              Select up to 50 sessions. Check warning icons for differences between recorded names
              and filenames. Sessions already in Paddiq are marked and skipped.
            </li>
            <li>
              Continue through <strong>Map drivers</strong>, <strong>Map karts</strong> and{' '}
              <strong>Map circuits</strong>. Every session needs a driver and kart; circuits are
              optional.
            </li>
            <li>
              Review the batch and any individual overrides, then confirm the import. The results
              show successes, duplicates and errors, with links to available Paddiq sessions.
            </li>
          </ol>
          <p>
            A driver means a Paddiq driver profile, not the person signed in to Link. If a profile
            is missing, authorized team owners and coaches can create it during mapping. Choose a
            historical driver for a record without a login, or a team-managed account when the
            driver needs to sign in. Historical drivers do not use an account seat. Missing karts
            can also be created as current or historical vehicles. Review and confirm creation;
            created profiles remain available if a later transfer fails.
          </p>
        </div>
      </section>
      <section className="guide-step" id="mappings">
        <div>
          <h2>Match drivers, vehicles and tracks.</h2>
          <p>
            Open <strong>Mappings</strong>. The tables populate from names found in your session
            files. Match the <strong>Race Studio name</strong> on the left to the{' '}
            <strong>Paddiq name</strong> on the right, then <strong>Save</strong>. Use{' '}
            <strong>Unmapped</strong> to find names that still need attention. A vehicle maps to a
            Paddiq kart; a track can map to a circuit and layout or stay unmapped.
          </p>
          <p>
            For example, “Birelart 67” and “Birelart Cadet 67” can both match the same Paddiq kart.
            They are grouped into one match. Expand <strong>Other matching names</strong> to see
            earlier aliases. Saved rules not found in this scan are kept separately for future
            files; they do not mean another driver or kart was found today.
          </p>
          <p>
            Link reads recorded metadata first and uses Race Studio’s naming rules to fill missing
            names. Hover over names, file counts or status icons for details. Saved mappings and
            profile names appear from the local cache while Link checks for updates. These rules
            belong to this account and workspace on this computer. Rescanning preserves unsaved
            choices; saving and transferring check current access.
          </p>
          <p>
            In the transfer wizard, a bulk match applies to selected sessions with the same source
            name. Check individual assignments on the review step. Matching a name does not rename
            Race Studio or change assignments on sessions already in Paddiq.
          </p>
        </div>
      </section>
      <section className="guide-step" id="rename">
        <div>
          <h2>Rename a Race Studio driver or vehicle.</h2>
          <ol className="guide-instructions">
            <li>
              Choose the Paddiq match in <strong>Mappings</strong> and save it first.
            </li>
            <li>
              Click <strong>Rename</strong> beside the Race Studio driver or vehicle name.
            </li>
            <li>
              Edit <strong>New Race Studio name</strong>, or choose <strong>Use Paddiq name</strong>
              . Select <strong>Preview rename</strong> after changing the text.
            </li>
            <li>
              Review the affected sessions and any existing profile that will be reused, then choose{' '}
              <strong>Rename in Race Studio</strong>.
            </li>
          </ol>
          <p>
            Link backs up the database and updates the Race Studio profile and its linked session
            labels, including linked sessions in its trash. Original session files, filenames and
            Paddiq profiles stay unchanged. Earlier names remain matching aliases, so future exports
            reuse the renamed profile. Renaming is available for drivers and vehicles.
          </p>
        </div>
      </section>
      <section className="guide-step" id="export">
        <div>
          <h2>Export sessions to Race Studio.</h2>
          <ol className="guide-instructions">
            <li>
              Open <strong>Export</strong>. Available Paddiq sessions load automatically. Select up
              to 50 sessions with retained XRK/XRZ originals.
            </li>
            <li>
              Review driver and kart mappings, plus any optional circuit match. You can create
              missing Paddiq profiles in the mapping steps, just as when importing.
            </li>
            <li>
              Review the destination and assignments, then confirm the export. Check the results for
              registration, duplicates or an error on each session.
            </li>
          </ol>
          <p>
            Link saves the files and updates Race Studio’s existing database directly. It creates or
            reuses the Race Studio driver and vehicle profiles from the reviewed mappings; you do
            not need to create them separately. Link does not launch or control Race Studio.
          </p>
          <p>
            Existing recorded names are preserved. Blank driver or vehicle names in an exported copy
            are filled from the reviewed mapping; the Paddiq original stays unchanged. XRZ files are
            expanded to XRK. Existing sessions are skipped and unrelated files are never
            overwritten. JSON-only sessions cannot be reconstructed into an AiM original.
          </p>
        </div>
      </section>
      <section className="guide-step" id="automatic">
        <div>
          <h2>Automatically copy MyChron downloads.</h2>
          <ol className="guide-instructions">
            <li>
              In <strong>Export</strong>, turn on <strong>Automatically copy to Race Studio</strong>
              . Link checks the configured session folder and existing database.
            </li>
            <li>
              Review your MyChron’s driver and kart assignments. These take priority over saved name
              mappings. Missing driver or kart mappings wait for review; circuits are optional.
            </li>
            <li>
              Download a new session from the MyChron. Open the copy activity or pending count to
              check its Race Studio result. After fixing a missing mapping or a failed attempt,
              choose <strong>Retry pending imports</strong>.
            </li>
          </ol>
          <p>
            This applies to new downloads while enabled. It does not export an existing library
            retroactively; use manual Export for those sessions. Turning it off pauses pending Race
            Studio work while Paddiq uploads continue independently. If you change the session
            folder or database, review the new destination and switch the option off and on again.
          </p>
          <p>
            <strong>Uploaded</strong> confirms a Paddiq result. <strong>Registered</strong> confirms
            a Race Studio database entry. An upload can finish while Race Studio is still waiting
            for a mapping or retry.
          </p>
        </div>
      </section>
      <section className="guide-step" id="storage">
        <div>
          <h2>Check folders and naming.</h2>
          <p>
            In <strong>Storage &amp; copies</strong>, check the Race Studio session folder, database
            and <strong>Naming rules</strong>. Link reads Race Studio’s preferences. Change those
            preferences in Race Studio, then refresh this view.
          </p>
          <p>
            A Date / Driver layout puts each recording under its date and driver folder. A filename
            order of Driver → Vehicle → Track → Session type → Original filename joins those fields
            using the configured separator, such as an underscore. Your settings determine the
            actual layout. Reviewed Paddiq names supply export naming; an unmapped circuit keeps the
            recorded track name.
          </p>
          <p>
            <strong>Session file copies</strong> saves an additional local file without registering
            it in the database. <strong>Use for Link copies</strong> applies the displayed folder
            and layout to those copies and keeps their on/off setting. Use the Export toggle for
            automatic Race Studio registration. See{' '}
            <a href="/resources/paddiq-link-file-copies">session file copies and retries</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="troubleshoot">
        <div>
          <h2>Resolve a missing session or failed transfer.</h2>
          <ul>
            <li>
              <strong>Race Studio is missing from navigation:</strong> the integration is Windows
              only. Check that your installed Link build includes it.
            </li>
            <li>
              <strong>Rename is disabled:</strong> choose and save the Paddiq match first. After
              editing the new name, preview it again before applying.
            </li>
            <li>
              <strong>Copied or uploaded, but absent from Race Studio:</strong> check the Export
              activity for registration. A plain folder copy or cloud upload is a separate result.
            </li>
            <li>
              <strong>Registered, but the open Race Studio list is stale:</strong> its list can lag
              behind database changes. Immediate refresh without reopening Race Studio is not yet
              guaranteed. Link does not restart it. Re-exporting the session will not refresh that
              list.
            </li>
            <li>
              <strong>Database, file format or telemetry error:</strong> keep the originals, check
              the exact error and supported version, and retry after resolving it. Link keeps
              previously saved files and reviewed choices for a safe retry.
            </li>
            <li>
              <strong>Already in Paddiq or Race Studio:</strong> open the existing session. Copies
              and renamed files do not need another import. A trashed Race Studio session must be
              restored in Race Studio.
            </li>
          </ul>
          <p>
            If it still fails, <a href="/support">contact Paddiq support</a> with the Link and Race
            Studio versions, transfer direction and exact error. For account or network errors,
            follow <a href="/resources/paddiq-link-troubleshooting">Link troubleshooting</a>.
          </p>
        </div>
      </section>
    </LinkGuideLayout>
  );
}
